import React, { useState } from 'react'
import { Box, BoxProps, Typography } from '@mui/material'
import {
  Control,
  Controller,
  FieldValues,
  UseControllerProps
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import SetRateTypeSelectors, {
  SetRateTypeSelectorsProps
} from './SetRateTypeSelectors'
import { TextField } from '@material-ui/core'

interface Props<T extends FieldValues> extends SetRateTypeSelectorsProps<T> {
  priceController: Omit<UseControllerProps<T>, 'control'>
  control: Control<T>
  containerProps?: BoxProps
}

const SetRatePriceInput = <T extends FieldValues>({
  priceController,
  containerProps: { sx: containerSx, ...restContainerProps } = {
    sx: {}
  },
  ...props
}: Props<T>) => {
  const { t } = useTranslation()

  const [showMoreOptions, setShowMoreOptions] = useState(false)

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        ...containerSx
      }}
      {...restContainerProps}
    >
      <Controller
        control={props.control}
        rules={{
          required: {
            value: true,
            message: t('validations.required')
          },
          shouldUnregister: true
        }}
        {...priceController}
        render={({ field, fieldState: { error } }) => (
          <Box
            sx={{
              position: 'relative'
            }}
          >
            {error?.message && (
              <Typography
                sx={{
                  position: 'absolute',
                  top: '-1.5rem',
                  left: '0',
                  fontSize: '14px',
                  color: 'red'
                }}
              >
                {error.message}
              </Typography>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <label htmlFor='ratePrice'>
                <Typography
                  sx={{
                    fontSize: '18px'
                  }}
                >
                  {t('apps.rates.rateDataGrid.modal.fix_rate')}
                </Typography>
              </label>
              <TextField
                {...field}
                size='small'
                type='number'
                inputProps={{
                  min: 0
                }}
                sx={{
                  marginLeft: '1rem',
                  width: '10rem',
                  '.MuiOutlinedInput-input': {
                    color: (theme) => theme.palette.text.primary
                  }
                }}
              />
            </Box>
          </Box>
        )}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}
      >
        <Typography
          sx={{
            cursor: 'pointer'
          }}
          onClick={() => setShowMoreOptions(!showMoreOptions)}
        >
          {showMoreOptions
            ? t('buttons.Less_options')
            : t('buttons.More_options')}
        </Typography>
        {showMoreOptions && <SetRateTypeSelectors {...props} />}
      </Box>
    </Box>
  )
}
export default SetRatePriceInput
